/**
 * Security utilities for XSS prevention in the frontend
 * Provides HTML sanitization without requiring DOMPurify dependency
 */

/**
 * Escapes HTML special characters to prevent XSS
 * Use this for plain text that should not contain HTML
 * @param str - String to escape
 * @returns escaped string safe for HTML rendering
 */
export function escapeHtml(str: string): string {
  if (!str) return '';

  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return String(str).replace(/[&<>"']/g, (char) => htmlEscapes[char]);
}

/**
 * Allowed HTML tags for email content rendering
 */
const ALLOWED_TAGS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'a',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'div', 'span',
  'img',
]);

/**
 * Allowed attributes for HTML tags
 */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  'a': new Set(['href', 'title', 'target', 'rel']),
  'img': new Set(['src', 'alt', 'width', 'height']),
  'div': new Set(['class']),
  'span': new Set(['class']),
  'td': new Set(['colspan', 'rowspan']),
  'th': new Set(['colspan', 'rowspan']),
};

/**
 * Dangerous patterns that indicate potential XSS
 */
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
  /javascript:/gi,
  /data:/gi,
  /vbscript:/gi,
  /on\w+\s*=/gi,
  /expression\s*\(/gi,
  /url\s*\(\s*["']?\s*javascript:/gi,
  /<\/?iframe/gi,
  /<\/?object/gi,
  /<\/?embed/gi,
  /<\/?form/gi,
  /<\/?input/gi,
  /<\/?button/gi,
  /<\/?select/gi,
  /<\/?textarea/gi,
];

/**
 * Sanitizes HTML content for safe rendering with dangerouslySetInnerHTML
 * Removes script tags, event handlers, and dangerous protocols while preserving safe formatting
 * @param html - Raw HTML content
 * @param options - Configuration options
 * @returns sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(
  html: string,
  options: {
    allowedTags?: Set<string>;
    allowedAttrs?: Record<string, Set<string>>;
    stripAllScripts?: boolean;
  } = {}
): string {
  if (!html) return '';

  const {
    allowedTags = ALLOWED_TAGS,
    allowedAttrs = ALLOWED_ATTRS,
    stripAllScripts = true,
  } = options;

  let sanitized = html;

  // Step 1: Remove dangerous patterns first
  for (const pattern of DANGEROUS_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  // Step 2: Parse and sanitize HTML tags
  // Remove any tags not in the allowed list
  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)(?:\s+([^>]*))?(?:\s*\/?>)/gi;

  sanitized = sanitized.replace(tagPattern, (match, tagName, attributes) => {
    const normalizedTagName = tagName.toLowerCase();

    // Check if tag is allowed
    if (!allowedTags.has(normalizedTagName)) {
      return '';
    }

    // If no attributes, tag is safe
    if (!attributes) {
      return match;
    }

    // Sanitize attributes
    const sanitizedAttrs = sanitizeAttributes(attributes.trim(), normalizedTagName, allowedAttrs);

    if (sanitizedAttrs) {
      return `<${tagName}${sanitizedAttrs}>`;
    } else {
      // Tag exists but has no safe attributes
      return `<${tagName}>`;
    }
  });

  // Step 3: Clean up any remaining dangerous patterns that might have been introduced
  if (stripAllScripts) {
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/\son\w+\s*=/gi, ' data-ok=');
  }

  // Step 4: Ensure no javascript: URLs remain (in href, src, etc.)
  sanitized = sanitized.replace(/href\s*=\s*["']?\s*javascript:/gi, 'href="#" data-safe="1"');
  sanitized = sanitized.replace(/src\s*=\s*["']?\s*javascript:/gi, 'src="" data-safe="1"');
  sanitized = sanitized.replace(/action\s*=\s*["']?\s*javascript:/gi, 'action="#" data-safe="1"');

  // Step 5: Normalize multiple blank lines
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n');

  return sanitized.trim();
}

/**
 * Sanitizes HTML attributes for a given tag
 */
function sanitizeAttributes(
  attrsString: string,
  tagName: string,
  allowedAttrs: Record<string, Set<string>>
): string {
  if (!attrsString) return '';

  const allowedAttrsForTag = allowedAttrs[tagName] || new Set();
  const sanitizedParts: string[] = [];

  // Handle both space-separated and multi-attribute strings
  const attrPattern = /([a-zA-Z][a-zA-Z0-9-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/gi;

  let match;
  while ((match = attrPattern.exec(attrsString)) !== null) {
    const attrName = match[1].toLowerCase();
    const attrValue = match[2] || match[3] || match[4] || '';

    // Check if attribute is allowed for this tag
    if (!allowedAttrsForTag.has(attrName)) {
      continue;
    }

    // Special validation for certain attributes
    if (attrName === 'href' || attrName === 'src') {
      // Only allow http, https, and mailto protocols
      const trimmedValue = attrValue.trim().toLowerCase();
      if (trimmedValue &&
          !trimmedValue.startsWith('http://') &&
          !trimmedValue.startsWith('https://') &&
          !trimmedValue.startsWith('mailto:') &&
          !trimmedValue.startsWith('#') &&
          !trimmedValue.startsWith('/')) {
        continue;
      }
    }

    // Sanitize the attribute value (escape HTML)
    const safeValue = escapeHtml(attrValue);
    sanitizedParts.push(`${attrName}="${safeValue}"`);
  }

  if (sanitizedParts.length === 0) {
    return '';
  }

  return ' ' + sanitizedParts.join(' ');
}

/**
 * Converts newlines to <br> tags for plain text email content
 * This is safer than dangerouslySetInnerHTML for simple text formatting
 * @param text - Plain text with newlines
 * @returns HTML string with <br> tags
 */
export function textToHtml(text: string): string {
  if (!text) return '';
  return escapeHtml(text).replace(/\n/g, '<br>');
}

/**
 * Sanitizes email template content specifically
 * Email content may have more permissive formatting
 * @param content - Email content HTML
 * @returns sanitized email content
 */
export function sanitizeEmailContent(content: string): string {
  // For email content, we allow slightly more formatting
  // but still strip scripts and event handlers
  return sanitizeHtml(content, {
    allowedTags: new Set([
      ...ALLOWED_TAGS,
      'body', 'html', 'head', 'title', 'style',
      'center', 'font', 'table', 'tbody', 'tfoot', 'thead',
    ]),
    allowedAttrs: {
      ...ALLOWED_ATTRS,
      'body': new Set(['bgcolor', 'text', 'link', 'vlink', 'alink']),
      'font': new Set(['face', 'size', 'color']),
      'td': new Set(['bgcolor', 'colspan', 'rowspan', 'width', 'height', 'valign']),
      'th': new Set(['bgcolor', 'colspan', 'rowspan', 'width', 'height', 'valign']),
    },
  });
}

/**
 * Validates that a URL is safe to include in HTML (for href/src attributes)
 * @param url - URL to validate
 * @returns true if safe
 */
export function isUrlSafe(url: string): boolean {
  if (!url) return true;

  const trimmed = url.trim().toLowerCase();

  // Allow relative URLs starting with / or #
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) {
    return true;
  }

  // Allow http/https URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return true;
  }

  // Allow mailto links
  if (trimmed.startsWith('mailto:')) {
    return true;
  }

  return false;
}